import { configureSignIn, retryUnlessClientError } from '@lunary/ui-web/query-error'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { BrandLoader } from '@lunary/ui-web/brand-loader'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { rootRoute } from './routes/root'
import { indexRoute } from './routes/index'
import { dailyCardRoute } from './routes/daily-card'
import { authRoute } from './routes/auth'
import { authCallbackRoute } from './routes/auth-callback'
import { deckRoute } from './routes/deck'
import { historyRoute } from './routes/history'
import { profileLunarsRoute, profileRoute, profileSettingsRoute, topupRoute } from './routes/profile'
import { payResultRoute } from './routes/pay'
import { claimGiftRoute, giveGiftRoute, myGiftsRoute } from './routes/gifts'
import { myDayRoute } from './routes/my-day'
import { pickRoute } from './routes/pick'
import { readingsRoute } from './routes/readings'
import { readingChatRoute } from './routes/readings-chat'
import { expertReadingRoute } from './routes/readings-client'
import { newReadingRoute } from './routes/readings-new'
import { readingRoute } from './routes/reading'
import { initAnalytics, trackHit } from '@lunary/ui-web/analytics'
import { initAppUpdate, navigateIfOutdated } from '@lunary/ui-web/app-update'
import { initDeck } from '@lunary/ui-web/deck'
import { setBrandStage } from '@lunary/ui-web/brand-stage'
import { getAccessToken } from './shared/api/client'
import { screenTransition } from './shared/navigation/screen-transition'
import { initTheme } from './shared/theme/theme'
import '@lunary/ui-web/fonts.css'
import '@lunary/ui-web/scroll.css'
import '@lunary/ui-web/touch-inputs.css'
import './styles/globals.css'

setBrandStage(import.meta.env.VITE_APP_STAGE)
initTheme()
initAppUpdate({ buildId: __BUILD_ID__ })
initDeck('/api/v1', getAccessToken)
initAnalytics(import.meta.env.VITE_YM_ID, 'web')

configureSignIn(() => window.location.assign('/profile'))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: retryUnlessClientError,
      staleTime: 30_000,
    },
  },
})

const routeTree = rootRoute.addChildren([indexRoute, dailyCardRoute, deckRoute, historyRoute, myDayRoute, pickRoute, readingsRoute, newReadingRoute, giveGiftRoute, myGiftsRoute, readingChatRoute, expertReadingRoute, readingRoute, profileRoute, profileLunarsRoute, profileSettingsRoute, topupRoute, payResultRoute, claimGiftRoute, authRoute, authCallbackRoute])

const router = createRouter({ routeTree, defaultPreload: 'intent', defaultViewTransition: screenTransition, defaultPendingComponent: () => <BrandLoader /> })

router.subscribe('onBeforeNavigate', ({ toLocation, pathChanged }) => {
  if (pathChanged) navigateIfOutdated(toLocation.href)
})

router.subscribe('onResolved', ({ toLocation }) => trackHit(toLocation.pathname))

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  )
}

const root = document.getElementById('root')
if (root) {
  createRoot(root).render(
    <StrictMode>
      <App />
    </StrictMode>,
  )
}
